import React from 'react';
import moment from 'moment';
import { useMutation } from 'react-apollo';
import { injectIntl } from 'react-intl';
import MediaQuery from 'react-responsive';
import Config from 'config';
import * as Sentry from '@sentry/browser';
import Menu from '../../../menu';
import styles from './index-module.css';
import headers from '../locale';
import messages from './locale';
import HDots from '../../../../assets/h-dots.svg';
import {
  isCheckedIn,
  isCheckedOut,
} from '../check-in-state';

import orderItemCheckInMutation from '../order-item-checkin.gql';
import orderItemCheckOutMutation from '../order-item-checkout.gql';

import { SMALL_DEVICE_BREAK_POINT, CANCELLED } from '../consts';

const OrderRow = injectIntl((props) => {
  const [orderItemCheckInOut] = useMutation(orderItemCheckOutMutation,
    {
      variables: {
        id: props.eventId,
      },
      onCompleted: () => {
        props.trackAmplitude('Add-ons: Order Not Redeemed');
        props.handleOrderItemStatusChange();
      },
      onError: error => Sentry.captureException(error),
    }
  );

  const [orderItemIn] = useMutation(orderItemCheckInMutation,
    {
      variables: {
        id: props.eventId,
      },
      onCompleted: () => {
        props.trackAmplitude('Add-ons: Order Not Redeemed');
        props.handleOrderItemStatusChange();
      },
      onError: error => Sentry.captureException(error),
    }
  );

  const renderActions = (addOnOrderItem) => {
    const customButtonIcon = <HDots className={styles.dotsIcon} />;
    return (
      <Menu
        intl={props.intl}
        options={addOnOptions(addOnOrderItem)}
        customButton={customButtonIcon}
      />
    );
  }

  const addOnOptions = (addOnOrderItem) => {
    const { checkInState, id } = addOnOrderItem;
    const orderItemCheckInPayload = {variables: { id }};

    const orderItemCheckIn = () => {
      orderItemIn(orderItemCheckInPayload);
    };

    const orderItemCheckOut = () => {
      orderItemCheckInOut(orderItemCheckInPayload);
    };

    return [
      {
        show: isCheckedIn(checkInState),
        text: props.intl.formatMessage(messages.markAsNotRedeemed),
        classNames: styles.actionOption,
        callback: orderItemCheckOut
      },
      {
        show: isCheckedOut(checkInState),
        text: props.intl.formatMessage(messages.markAsRedeemed),
        classNames: styles.actionOption,
        callback: orderItemCheckIn
      },
      {
        show: true,
        text: props.intl.formatMessage(messages.viewOrder),
        classNames: styles.actionOption,
        href: `/dashboard/order/${addOnOrderItem.order.id}`,
        callback: () => {} // Menu component expects callback function.
      },
      {
        show: true,
        text: props.intl.formatMessage(messages.messageBuyer),
        classNames: styles.actionOption,
        href: `${Config.api.host}/dashboard/conversations/withUser/${
          addOnOrderItem.primaryBuyer.id
        }`,
        callback: () => {} // Menu component expects callback function.
      }
    ];
  }

  const renderBuyerName = (buyer, primaryBuyer) => {
    return (
      <div className={styles.buyerDetails} data-cs-mask>
        <div className={styles.buyerName}>{buyer.name}</div>
        <div className={styles.buyerEmail}>{buyer.email}</div>
        <div className={styles.buyerEmail}>{props.intl.formatMessage(messages.Buyer)}: {primaryBuyer.email}</div>
      </div>
    );
  }

  const renderAddOnName = (orderItem) => {
    const addOnName = orderItem.rate.name;
    const addOnOption = orderItem.optionName || '';
    return (
      <div className={styles.addOnName}>
        {addOnName}
        <div className={styles.buyerEmail}>{addOnOption}</div>
      </div>
    );
  }

  const renderCheckInState = (checkInState) => {
    let dotClass = styles.greenDot;
    let messageClass = styles.green;
    let message = props.intl.formatMessage(messages.checkedIn);
    if (isCheckedOut(checkInState)) {
      dotClass = styles.greyDot;
      messageClass = styles.grey;
      message = props.intl.formatMessage(messages.checkedOut)
    }

    return (
      <span className={messageClass}>
        <div className={dotClass} />
        {message}
      </span>
    );
  }

  const renderCheckInDropdown = (orderItem) => {
    return (
      <div className={styles.checkInState}>
        {orderItem.state === CANCELLED ?
          props.intl.formatMessage(messages.cancelled) :
          renderCheckInState(orderItem.checkInState)
        }
      </div>
    );
  }

  const renderMobileRow = (addOnOrderItem) => {
    const { buyer, primaryBuyer, createdAt, order } = addOnOrderItem;
    return (
      <div className={styles.mobileRow}>
        <div className={styles.buyerHeader}>
          <div className={styles.buyerLabel}>
            <div className={styles.label}>
              {props.intl.formatMessage(headers.attendee)}
            </div>
            {renderBuyerName(buyer, primaryBuyer)}
          </div>

          <div className={styles.actionsBox}>
            {renderActions(addOnOrderItem)}
          </div>
        </div>
        <div>
          <div className={styles.label}>
            {props.intl.formatMessage(headers.addOn)}
          </div>
          {renderAddOnName(addOnOrderItem)}
        </div>
        <div className={styles.eventTitle}>
          <div className={styles.label}>
            {props.intl.formatMessage(headers.eventName)}
          </div>
          {order.event.title}
        </div>
        <div className={styles.orderDate}>
          <div className={styles.label}>
            {props.intl.formatMessage(headers.date)}
          </div>
          {formatDate(createdAt)}
        </div>
        <div>
          <div className={styles.label}>
            {props.intl.formatMessage(headers.checkInState)}
          </div>
          {renderCheckInDropdown(addOnOrderItem)}
        </div>
      </div>
    );
  }

  const renderDesktopRow = (addOnOrderItem) => {
    const { createdAt, buyer, primaryBuyer, order } = addOnOrderItem;
    return (
      <div className={styles.row}>
        {renderBuyerName(buyer, primaryBuyer)}
        {renderAddOnName(addOnOrderItem)}
        <div className={styles.eventTitle}>{order.event.title}</div>
        <div className={styles.orderDate}>
          {formatDate(createdAt)}  
        </div>
        {renderCheckInDropdown(addOnOrderItem)}
        <div className={styles.actionsBox}>
          {renderActions(addOnOrderItem)}
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    return props.intl.formatDate(moment(date), {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  }

  return(
    <div className={styles.orderRow}>
      <MediaQuery maxWidth={SMALL_DEVICE_BREAK_POINT}>
        <div>
          {renderMobileRow(props.addOnOrderItem)}
        </div>
      </MediaQuery>
      <MediaQuery minWidth={SMALL_DEVICE_BREAK_POINT + 1}>
        <div>
          {renderDesktopRow(props.addOnOrderItem)}
        </div>
      </MediaQuery>
  </div>)
});

export default OrderRow;
